import { queryOptions } from '@tanstack/react-query';
import { getAccountSettings } from '../endpoints/settings';
import { AccountSettingsSchema, type AccountSettings } from '../schemas/settings';
import { DeviceSettingsSchema, type DeviceSettings } from '../schemas/device-settings';
import { readDeviceSettings } from '../storage/device-settings';

// Re-export the domain types through the queries layer so hooks and the setting
// registry can type themselves without importing `@/api/schemas/*` (forbidden
// outside src/api by the data-layer seam rule).
export type { AccountSettings } from '../schemas/settings';
export type { DeviceSettings } from '../schemas/device-settings';

export const SETTINGS_QUERY_KEYS = {
  all: ['settings'] as const,
  account: () => ['settings', 'account'] as const,
  device: () => ['settings', 'device'] as const,
} as const;

/** Fully-defaulted documents, exposed so consumers have a stable value pre-load. */
export const ACCOUNT_SETTINGS_DEFAULTS: AccountSettings = AccountSettingsSchema.parse({});
export const DEVICE_SETTINGS_DEFAULTS: DeviceSettings = DeviceSettingsSchema.parse({});

/**
 * Account settings live on the server and follow the user across devices.
 * Persisted (meta.persist) so the settings screen paints from disk on cold boot.
 */
export const accountSettingsQueryOptions = () =>
  queryOptions({
    queryKey: SETTINGS_QUERY_KEYS.account(),
    queryFn: getAccountSettings,
    staleTime: 5 * 60_000, // 5 minutes
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    meta: { persist: true },
  });

/**
 * Device settings live in local storage (per-install). Read from the on-disk
 * blob, never the network, and NOT persisted through React Query — AsyncStorage
 * is already the source of truth.
 */
export const deviceSettingsQueryOptions = () =>
  queryOptions({
    queryKey: SETTINGS_QUERY_KEYS.device(),
    queryFn: readDeviceSettings,
    staleTime: Infinity, // local disk is authoritative; never goes stale
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    meta: { persist: false },
  });
